import { useState, useCallback, useRef, useEffect } from "react";
import { X, Save, Upload, Info } from "lucide-react"; // Add Info icon
import { LoadingSpinner } from "../LoadingSpinner";
import { useTemplates } from "../../hooks/useTemplates";
import { useClickOutside } from "../../hooks/useClickOutside";
import useEscapeKey from "../../hooks/useEscapeKey";
import { useNotifications } from "../../contexts/NotificationContext";
import useStore from "../../store/useStore";
import { motion } from "framer-motion";
import ValidatedInput from "../shared/ValidatedInput";
import { DEFAULT_VALIDATION_CONFIG, validateAndSanitizeInput } from "../../utils/validation/inputValidation";

interface SaveTemplateDialogProps {
  onClose: () => void;
  onSaveSuccess: () => void; // Add onSaveSuccess prop
}

export default function SaveTemplateDialog({
  onClose,
  onSaveSuccess,
}: SaveTemplateDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState("");
  const [category, setCategory] = useState("Custom");
  const [isLoading, setIsLoading] = useState(false); // For save/update operation
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false); // For loading dropdown templates
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { nodes, edges } = useStore();
  const { 
    templates: existingTemplates, 
    loadTemplates, 
    promoteToTemplate, 
    updateTemplate 
  } = useTemplates();
  const { showNotification } = useNotifications();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("new"); // 'new' or template ID

  // Fetch existing templates for the dropdown
  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoadingTemplates(true);
      await loadTemplates();
      setIsLoadingTemplates(false);
    };
    fetchTemplates();
  }, [loadTemplates]);

  // Handle selection change in the dropdown
  const handleTemplateSelectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = event.target.value;
    setSelectedTemplateId(templateId);

    if (templateId === "new") {
      // Reset form for new template
      setName("");
      setDescription("");
      setCategory("Custom");
      setPreviewImage(null);
      setPreviewFile(null);
      setNameError("");
    } else {
      // Load data for selected existing template
      const selectedTemplate = existingTemplates.find(t => t.id === templateId);
      if (selectedTemplate) {
        setName(selectedTemplate.name);
        setDescription(selectedTemplate.description);
        setCategory(selectedTemplate.category);
        setPreviewImage(selectedTemplate.preview); // Display existing preview
        setPreviewFile(null); // Reset file input, user must choose a new file to update
        setNameError("");
      }
    }
  };

  const handleSave = useCallback(async () => {
    try {
      if (!name.trim()) {
        setNameError("Please enter a template name");
        return;
      }
      setIsLoading(true);

      const sanitizedName = validateAndSanitizeInput(name, DEFAULT_VALIDATION_CONFIG.templateName);
      const sanitizedDescription = validateAndSanitizeInput(description, DEFAULT_VALIDATION_CONFIG.templateDescription);
      
      // Deselect nodes and edges before saving
      const deselectedNodes = nodes.map(node => ({ ...node, selected: false }));
      const deselectedEdges = edges.map(edge => ({ ...edge, selected: false }));
      // Use the deselected versions
      const plainNodes = JSON.parse(JSON.stringify(deselectedNodes));
      const plainEdges = JSON.parse(JSON.stringify(deselectedEdges));


      try {
        if (selectedTemplateId === "new") {
          // Create new template
          await promoteToTemplate({
            name: sanitizedName,
            description: sanitizedDescription,
            category,
            nodes: plainNodes,
            edges: plainEdges,
            previewFile: previewFile || undefined,
          });
          showNotification("success", "Template saved successfully");
        } else {
          // Update existing template
          await updateTemplate(selectedTemplateId, {
            name: sanitizedName,
            description: sanitizedDescription,
            category,
            nodes: plainNodes,
            edges: plainEdges,
            previewFile: previewFile || undefined, // Pass file only if a new one was selected
            // If previewFile is undefined, the service/API should keep the existing preview
          });
          showNotification("success", "Template updated successfully");
        }
        await loadTemplates(); // Refresh the list after save/update
        onSaveSuccess();
        onClose();
      } catch (error) {
        const action = selectedTemplateId === "new" ? "save" : "update";
        console.error(`Error ${action}ing template:`, error);
        showNotification("error", `Failed to ${action} template`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    name, 
    description, 
    category, 
    nodes, 
    edges, 
    previewFile, 
    promoteToTemplate, 
    updateTemplate, // Add updateTemplate
    showNotification, 
    onSaveSuccess, 
    onClose, 
    selectedTemplateId, // Add selectedTemplateId
    loadTemplates // Add loadTemplates
  ]);

  const dialogRef = useClickOutside<HTMLDivElement>(onClose);
  useEscapeKey(onClose); // Use escape key to close the dialog

  return (
    <motion.div 
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
          <motion.div 
            ref={dialogRef} 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-lg mx-4 border border-gray-200 dark:border-gray-700"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className="px-6 pt-5 rounded-t-lg">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Save as Template
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                {/* Template Selection Dropdown */}
                <div>
                  <label htmlFor="template-action" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Action
                  </label>
                  <select
                    id="template-action"
                    value={selectedTemplateId}
                    onChange={handleTemplateSelectionChange}
                    disabled={isLoadingTemplates}
                    className="w-full px-3 pr-4 py-2 border border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out disabled:opacity-50"
                  >
                    <option value="new">Save as new template</option>
                    {isLoadingTemplates ? (
                      <option disabled>Loading templates...</option>
                    ) : (
                      existingTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          Update "{template.name}"
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {/* Preview Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Preview Image
                  </label>
                  <div className="flex items-center gap-4">
                    {previewImage ? (
                      <div className="w-32 h-20 border rounded-lg overflow-hidden">
                        <img
                          src={previewImage}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-20 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                        <Upload className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setPreviewFile(file);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setPreviewImage(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Choose file
                    </button>
                  </div>
                </div>

                <ValidatedInput
                  id="template-name"
                  label="Template Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) setNameError("");
                  }}
                  validationConfig={DEFAULT_VALIDATION_CONFIG.templateName}
                  placeholder="Enter template name"
                  errorMessage={nameError}
                  onBlur={() => {
                    if (!name.trim()) {
                      setNameError("Template name is required");
                    }
                  }}
                />

                <ValidatedInput
                  id="template-description"
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  validationConfig={DEFAULT_VALIDATION_CONFIG.templateDescription}
                  placeholder="Describe your template"
                  isTextarea={true}
                  rows={3}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 pr-4 py-2 border border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
                  >
                    <option value="Custom">Custom</option>
                    <option value="Governance">Governance</option>
                    <option value="Performance">Performance</option>
                    <option value="CDP">CDP</option>
                    <option value="Retail">Retail</option>
                    <option value="Privacy">Privacy</option>
                  </select>
                </div>
              </div> {/* End of space-y-4 */}

              {/* Informational Note */}
              <div className="mt-6 p-3 bg-blue-50 dark:bg-gray-700/50 border border-blue-200 dark:border-gray-600 rounded-md text-sm text-blue-700 dark:text-blue-300 flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Note:</strong> Templates are starting points for new projects. Use them to quickly set up common configurations, not for ongoing project work.
                </span>
              </div>
            </div> {/* End of p-6 */}


            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 rounded-b-lg border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!name || isLoading}
                  className={`
                    px-4 py-2 text-sm font-medium rounded-md inline-flex items-center gap-2
                    ${
                      !name || isLoading
                        ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }
                  `}
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="text-gray-500 dark:text-gray-400" />
                      <span>{selectedTemplateId === 'new' ? 'Saving...' : 'Updating...'}</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>{selectedTemplateId === 'new' ? 'Save Template' : 'Update Template'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
    </motion.div>
  );
}
